import { useLayoutEffect, useRef, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  ACCEPT_LABELS,
  OnboardingStepBody,
  STEP_TITLE_FONT_CLASS,
  STEP_TITLES,
} from "./OnboardingStepContent";

const SILK_BG =
  "https://images.unsplash.com/photo-1707573090905-8465126a393e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcmVkJTIwYWJzdHJhY3QlMjBzaWxrJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc1NDMyNjF8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [1, 2, 3, 4, 5];

function OnboardingStepWizard({ currentStep }) {
  return (
    <nav
      className="border-b border-[rgba(255,255,255,0.08)] px-4 py-4 md:px-8 md:py-5"
      aria-label="Pasos antes de entrar en La Lista"
    >
      <p className="mb-4 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-[rgba(245,245,245,0.45)]">
        Reglas & Normas de acceso
      </p>
      <ol className="flex flex-wrap items-center justify-center gap-y-2">
        {STEPS.map((n) => {
          const done = n < currentStep;
          const active = n === currentStep;
          return (
            <li key={n} className="flex items-center">
              {n > 1 ? (
                <span
                  className={cn(
                    "mx-1.5 hidden h-px w-4 sm:mx-2 sm:inline-block sm:w-8",
                    done ? "bg-[var(--zx-accent)]/45" : "bg-[rgba(255,255,255,0.12)]"
                  )}
                  aria-hidden
                />
              ) : null}
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border font-mono text-xs tabular-nums transition-colors duration-300 sm:h-9 sm:w-9 sm:text-[13px]",
                  active &&
                    "border-[var(--zx-accent)] bg-[rgba(139,30,63,0.22)] text-[#f5f5f5] shadow-[0_0_0_1px_rgba(139,30,63,0.35)]",
                  done &&
                    !active &&
                    "border-[rgba(255,255,255,0.22)] bg-[rgba(255,255,255,0.06)] text-[rgba(245,245,245,0.72)]",
                  n > currentStep &&
                    "border-[rgba(255,255,255,0.1)] text-[rgba(245,245,245,0.28)]"
                )}
                aria-current={active ? "step" : undefined}
              >
                {n}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function OnboardingGateDesktop({
  currentStep,
  acceptedSteps,
  setAccepted,
  nextStep,
  prevStep,
  finish,
  canAdvance,
}) {
  const isLast = currentStep === 5;
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const viewportRef = useRef(null);
  const [slideH, setSlideH] = useState(260);
  const [viewportW, setViewportW] = useState(0);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof window === "undefined") return;
    const upd = () => setViewportW(Math.max(0, Math.round(el.getBoundingClientRect().width)));
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    window.addEventListener("resize", upd);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", upd);
    };
  }, [slideH]);

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const cap = Math.max(240, Math.floor(window.innerHeight * 0.68));
    const measure = () => {
      const titleEl = titleRef.current;
      const bodyEl = bodyRef.current;
      if (!titleEl || !bodyEl) return;
      const gap = 20;
      const h = Math.ceil(
        titleEl.getBoundingClientRect().height + gap + bodyEl.scrollHeight
      );
      setSlideH(Math.min(Math.max(200, h), cap));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    if (titleRef.current) ro.observe(titleRef.current);
    if (bodyRef.current) ro.observe(bodyRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [currentStep]);

  return (
    <div className="relative h-[100dvh] max-h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] text-[#f5f5f5]">
      <div
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.22]"
        style={{
          backgroundImage: `url(${SILK_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) saturate(85%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(139,30,63,0.14), transparent 58%), linear-gradient(180deg, rgba(10,10,10,0.45), rgba(10,10,10,0.92))",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[3] mix-blend-overlay opacity-25"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
        }}
        aria-hidden
      />

      <div className="relative z-10 flex h-full min-h-0 flex-col overflow-hidden px-4 py-5 md:px-8 md:py-6">
        <div className="mx-auto flex w-full max-w-3xl min-h-0 flex-1 flex-col justify-center">
          <div
            className="zx-card zx-card-static zx-glass flex max-h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.1)] shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
            data-testid="onboarding-desktop-card"
          >
            <OnboardingStepWizard currentStep={currentStep} />

            <div
              ref={viewportRef}
              className="min-h-0 w-full overflow-hidden transition-[height] duration-300 ease-out motion-reduce:transition-none"
              style={{ height: slideH }}
            >
              <div
                className="flex h-full shrink-0 items-start transition-transform duration-500 ease-out motion-reduce:transition-none"
                style={{
                  width:
                    viewportW > 0 ? `${viewportW * STEPS.length}px` : "100%",
                  transform:
                    viewportW > 0
                      ? `translateX(-${(currentStep - 1) * viewportW}px)`
                      : `translateX(-${((currentStep - 1) / STEPS.length) * 100}%)`,
                }}
              >
                {STEPS.map((step) => (
                  <section
                    key={step}
                    className="box-border flex h-full shrink-0 flex-col overflow-hidden"
                    style={{
                      flex: viewportW > 0 ? `0 0 ${viewportW}px` : "0 0 100%",
                      width: viewportW > 0 ? `${viewportW}px` : "100%",
                      minWidth: viewportW > 0 ? `${viewportW}px` : undefined,
                    }}
                    aria-hidden={step !== currentStep}
                    id={`onboarding-panel-${step}`}
                  >
                    <div className="flex h-full min-h-0 flex-col px-6 pb-3 pt-5 md:px-10 md:pb-4 md:pt-6">
                      <h2
                        ref={step === currentStep ? titleRef : null}
                        className={cn(
                          STEP_TITLE_FONT_CLASS,
                          "shrink-0 text-2xl font-normal leading-tight tracking-tight text-[#f5f5f5] md:text-[1.75rem]"
                        )}
                      >
                        {STEP_TITLES[step]}
                      </h2>
                      <div
                        ref={step === currentStep ? bodyRef : null}
                        key={step === currentStep ? `active-${currentStep}` : `stash-${step}`}
                        className={cn(
                          "zx-no-scrollbar mt-5 min-h-0 flex-1 overflow-y-auto pr-1 md:mt-6",
                          step === currentStep
                            ? "zx-page-enter motion-reduce:animate-none"
                            : ""
                        )}
                      >
                        <OnboardingStepBody step={step} layout="desktop" />
                      </div>
                    </div>
                  </section>
                ))}
              </div>
            </div>

            <footer className="shrink-0 border-t border-[rgba(255,255,255,0.08)] bg-[rgba(5,5,5,0.35)] px-6 py-4 backdrop-blur-md md:px-10 md:py-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={`accept-desktop-${currentStep}`}
                    checked={acceptedSteps[currentStep]}
                    onCheckedChange={(v) =>
                      setAccepted(currentStep, v === true)
                    }
                    className="mt-0.5 border-[rgba(255,255,255,0.35)] data-[state=checked]:bg-[var(--zx-accent)] data-[state=checked]:border-[var(--zx-accent)]"
                  />
                  <Label
                    htmlFor={`accept-desktop-${currentStep}`}
                    className="cursor-pointer text-left font-mono text-xs leading-snug text-[rgba(245,245,245,0.75)]"
                  >
                    {ACCEPT_LABELS[currentStep]}
                  </Label>
                </div>

                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:gap-3">
                  {currentStep > 1 ? (
                    <button
                      type="button"
                      className="zx-btn-ghost max-w-none sm:min-w-[140px]"
                      onClick={prevStep}
                    >
                      Atrás
                    </button>
                  ) : (
                    <span className="hidden sm:block sm:min-w-[140px]" aria-hidden />
                  )}
                  {isLast ? (
                    <button
                      type="button"
                      className="zx-btn-primary max-w-none sm:min-w-[200px]"
                      disabled={!acceptedSteps[5]}
                      onClick={finish}
                    >
                      Entrar en La Lista
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="zx-btn-primary max-w-none sm:min-w-[200px]"
                      disabled={!canAdvance}
                      onClick={nextStep}
                    >
                      Continuar
                    </button>
                  )}
                </div>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
